import { useEffect, useRef, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { socialMediaApi } from '../api';

interface LinkedInMention {
  name: string;
  urn: string;
  type: 'organization';
}

interface LinkedInOrg {
  id: string;
  name: string;
  urn: string;
}

interface LinkedInMentionTextareaProps {
  value: string;
  onChange: (text: string) => void;
  mentions: LinkedInMention[];
  onMentionsChange: (mentions: LinkedInMention[]) => void;
  /** Vault Holder ID — required for the LinkedIn org search API. */
  holderId: string | null;
  accountId: string | undefined;
  disabled?: boolean;
  rows?: number;
  placeholder?: string;
  className?: string;
}

export function LinkedInMentionTextarea({
  value,
  onChange,
  mentions,
  onMentionsChange,
  holderId,
  accountId,
  disabled = false,
  rows = 4,
  placeholder = 'Post content will appear here…',
  className,
}: LinkedInMentionTextareaProps) {
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionTriggerStart, setMentionTriggerStart] = useState<number>(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [results, setResults] = useState<LinkedInOrg[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Debounced org search triggered by mentionQuery state
  useEffect(() => {
    if (mentionQuery === null || mentionQuery.length < 2 || !accountId || !holderId) {
      setResults([]);
      setShowDropdown(false);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      setSearchError(null);
      try {
        const { orgs } = await socialMediaApi.searchLinkedInOrgs(holderId, mentionQuery, accountId);
        setResults(orgs);
        setShowDropdown(orgs.length > 0);
      } catch {
        setSearchError('Organization search unavailable');
        setShowDropdown(false);
      } finally {
        setIsSearching(false);
      }
    }, 350);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [mentionQuery, accountId, holderId]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
        setMentionQuery(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    const cursorPos = e.target.selectionStart ?? newText.length;

    // Detect if cursor is inside an @-triggered mention query
    const textBeforeCursor = newText.slice(0, cursorPos);
    const atMatch = textBeforeCursor.match(/@([^\s@]*)$/);

    if (atMatch) {
      setMentionQuery(atMatch[1]);
      setMentionTriggerStart(cursorPos - atMatch[1].length - 1);
    } else {
      setMentionQuery(null);
      setShowDropdown(false);
    }

    onChange(newText);

    // Auto-clean mentions whose names are no longer present in the new text
    if (mentions.length > 0) {
      const filtered = mentions.filter((m) => newText.includes(m.name));
      if (filtered.length !== mentions.length) {
        onMentionsChange(filtered);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape' && showDropdown) {
      setShowDropdown(false);
      setMentionQuery(null);
    }
  };

  const handleOrgSelect = (org: LinkedInOrg) => {
    // Replace @query in the textarea with the org name (no @ prefix in final text)
    const before = value.slice(0, mentionTriggerStart);
    const after = value.slice(mentionTriggerStart + 1 + (mentionQuery?.length ?? 0));
    const newText = before + org.name + after;

    // Guard: avoid duplicate mentions for the same URN
    if (!mentions.some((m) => m.urn === org.urn)) {
      onMentionsChange([...mentions, { name: org.name, urn: org.urn, type: 'organization' }]);
    }

    onChange(newText);
    setMentionQuery(null);
    setShowDropdown(false);
    setResults([]);

    // Restore focus and place cursor after the inserted name
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursor = mentionTriggerStart + org.name.length;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursor, newCursor);
      }
    }, 0);
  };

  const removeMention = (urn: string) => {
    onMentionsChange(mentions.filter((m) => m.urn !== urn));
  };

  return (
    <div ref={containerRef} className="space-y-1.5">
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          rows={rows}
          disabled={disabled}
          readOnly={disabled}
          placeholder={placeholder}
          className={cn(
            'text-sm resize-none bg-background',
            'w-full text-[14px] rounded-[10px] outline-none transition-all duration-150 shadow-social-card',
            'px-3 py-3 !font-mono',
            'text-primary',
            'placeholder-[#171717]',
            className,
          )}
        />

        {/* Loading spinner shown while searching */}
        {isSearching && (
          <div className="absolute bottom-2 right-2">
            <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Org search dropdown */}
        {showDropdown && results.length > 0 && (
          <div className="absolute top-full left-0 right-0 z-50 mt-1 rounded-md border border-border bg-popover shadow-md overflow-y-auto max-h-44">
            {results.map((org) => (
              <button
                key={org.id}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleOrgSelect(org);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-accent transition-colors"
              >
                <div className="h-6 w-6 rounded bg-blue-600 flex items-center justify-center flex-shrink-0">
                  <span className="text-[9px] font-bold text-white">in</span>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-foreground truncate">{org.name}</p>
                  <p className="text-[10px] text-muted-foreground truncate">{org.urn}</p>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Empty state */}
        {showDropdown &&
          results.length === 0 &&
          (mentionQuery?.length ?? 0) >= 2 &&
          !isSearching && (
            <div className="absolute top-full left-0 right-0 z-50 mt-1 rounded-md border border-border bg-popover shadow-md px-3 py-2">
              <p className="text-[10px] text-muted-foreground">
                No organizations found. Try the company vanityName (e.g. &quot;microsoft&quot;).
              </p>
            </div>
          )}
      </div>

      {searchError && <p className="text-[10px] text-destructive">{searchError}</p>}

      {/* Mention chips */}
      {mentions.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {mentions.map((mention) => (
            <span
              key={mention.urn}
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5',
                'bg-blue-50 dark:bg-blue-950/30 text-xs text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800',
              )}
            >
              {mention.name}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeMention(mention.urn)}
                  aria-label={`Remove mention of ${mention.name}`}
                  className="text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Hint shown while typing @mention query */}
      {mentionQuery !== null && mentionQuery.length < 2 && (
        <p className="text-[10px] text-muted-foreground">
          Keep typing to search for an organization…
        </p>
      )}
    </div>
  );
}
